import {
  Coordinates,
  DisponibilitiesQueryParams,
  RideType,
  ValidFormValues,
  ValidOneWayFormValues,
} from "@/interfaces/searchBlock";
import { SelectedAgency, SelectedPlace } from "@/interfaces/searchBlock";
import { formatDateAndTimeToDateTime } from "../children/dateTimePicker/helpers/timeSelector";
import { VehicleType } from "../children/vehicleTypeSelection/VehicleTypeSelection";
import { getPlaceCoordinates } from "./getPlaceCoordinates";
import { PlacePredictionService } from "../children/autocomplete/hooks/usePlacePrediction";
import { PeliasService } from "../children/autocomplete/hooks/usePeliasPlaces";
import { getPeliasCoordinates } from "./getPeliasCoordinates";
import { computeOneWayStartCoordinates } from "./computeOneWayStartCoordinates";
import { computeRoundTripStartCoordinates } from "./computeRoundTripStartCoordinates";

const usePelias = process.env.NEXT_PUBLIC_PELIAS === "true";

const maxAgenciesNumberByRideType = {
  [RideType.ROUND_TRIP]: {
    pickup: 4,
    dropoff: 4,
  },
  [RideType.ONE_WAY]: {
    pickup: 3,
    dropoff: 5,
  },
};

const vehicleTypeParams = {
  [VehicleType.CAR]: { VehicleType: VehicleType.CAR },
  [VehicleType.TRUCK]: { VehicleType: VehicleType.TRUCK },
  [VehicleType.CAR_WITHOUT_LICENCE]: {
    VehicleType: VehicleType.CAR,
    IsOnlyWithoutLicense: true,
  },
};

const isOneWayTrip = (
  formValues: ValidFormValues
): formValues is ValidOneWayFormValues => formValues.isOneWayTrip;

const isAgencyPlace = (
  selectedPlace: SelectedPlace | null
): selectedPlace is SelectedAgency => selectedPlace?.isAgency ?? false;

export const computeDisponibilitiesQueryParams = async (
  placePredictionService: PlacePredictionService | null,
  googleMapsPlaceService: google.maps.places.PlacesService,
  googleMapsRefreshSessionToken: () => void,
  formValues: ValidFormValues,
  coordinates?: Coordinates,
  peliasService: PeliasService | null = null
) => {
  const { startSelectedPlace, endSelectedPlace } = isOneWayTrip(formValues)
    ? {
        startSelectedPlace: formValues.oneWayStart,
        endSelectedPlace: formValues.oneWayEnd,
      }
    : {
        startSelectedPlace: formValues.roundTrip,
        endSelectedPlace: null,
      };

  const { startPlaceId, agencyLatitude, agencyLongitude } = isAgencyPlace(
    startSelectedPlace
  )
    ? {
        startPlaceId: null,
        agencyLatitude: startSelectedPlace.latitude,
        agencyLongitude: startSelectedPlace.longitude,
      }
    : {
        startPlaceId: startSelectedPlace.placeId,
        agencyLatitude: undefined,
        agencyLongitude: undefined,
      };

  const pickupRoundTripMaxNumberOfAgencies =
    maxAgenciesNumberByRideType[RideType.ROUND_TRIP].pickup;
  const radius = 50;

  const endPlaceId = isAgencyPlace(endSelectedPlace)
    ? null
    : endSelectedPlace?.placeId ?? null;

  const startPlaceCoordinates = usePelias
    ? await getPeliasCoordinates({
        peliasService,
        placeId: startPlaceId,
        latitude:
          agencyLatitude ??
          startSelectedPlace.latitude ??
          coordinates?.pickupLatitude.toString(),
        longitude:
          agencyLongitude ??
          startSelectedPlace.longitude ??
          coordinates?.pickupLongitude.toString(),
      })
    : await getPlaceCoordinates({
        placePredictionService,
        placeId: startPlaceId,
        latitude: agencyLatitude ?? coordinates?.pickupLatitude,
        longitude: agencyLongitude ?? coordinates?.pickupLongitude,
      });

  let disponibilitiesQueryParams: DisponibilitiesQueryParams = {
    RideType: RideType.ROUND_TRIP,
    "Pickup.Latitude": computeRoundTripStartCoordinates(
      startSelectedPlace,
      startPlaceCoordinates
    ).computedLatitude,
    "Pickup.Longitude": computeRoundTripStartCoordinates(
      startSelectedPlace,
      startPlaceCoordinates
    ).computedLongitude,
    // TODO : export this parameter to Strapi
    "Pickup.MaxNumberOfAgencies": pickupRoundTripMaxNumberOfAgencies,
    Radius: radius,
    ...vehicleTypeParams[formValues.vehicleType],
    PickupDate: formatDateAndTimeToDateTime(formValues.startDate),
    DropoffDate: formatDateAndTimeToDateTime(formValues.endDate),
    pickupDescription: isOneWayTrip(formValues)
      ? formValues.oneWayStart.description
      : formValues.roundTrip.description,
    isAgency: isAgencyPlace(startSelectedPlace),
  };

  if (formValues.companyRateCode !== undefined) {
    disponibilitiesQueryParams.companyRateCode = formValues.companyRateCode;
  }

  if (formValues.sid !== undefined && formValues.sid) {
    disponibilitiesQueryParams.sid = formValues.sid;
  }

  if (isOneWayTrip(formValues)) {
    const endPlaceCoordinates = usePelias
      ? await getPeliasCoordinates({
          peliasService,
          placeId: endPlaceId,
          latitude: endSelectedPlace?.latitude ?? coordinates?.dropoffLatitude,
          longitude:
            endSelectedPlace?.longitude ?? coordinates?.dropoffLongitude,
        })
      : await getPlaceCoordinates({
          placePredictionService,
          placeId: endPlaceId,
          latitude: coordinates?.dropoffLatitude,
          longitude: coordinates?.dropoffLongitude,
        });

    const computedStartPlaceCoordinates = computeOneWayStartCoordinates(
      startSelectedPlace,
      startPlaceCoordinates
    );

    disponibilitiesQueryParams = {
      ...disponibilitiesQueryParams,
      RideType: RideType.ONE_WAY,
      "Pickup.MaxNumberOfAgencies":
        maxAgenciesNumberByRideType[RideType.ONE_WAY].pickup,
      ["Pickup.Latitude"]: computedStartPlaceCoordinates.computedLatitude,
      ["Pickup.Longitude"]: computedStartPlaceCoordinates.computedLongitude,
      ["Dropoff.Latitude"]: endPlaceCoordinates.computedLatitude,
      ["Dropoff.Longitude"]: endPlaceCoordinates.computedLongitude,
      ["Dropoff.MaxNumberOfAgencies"]:
        maxAgenciesNumberByRideType[RideType.ONE_WAY].dropoff,
      dropoffDescription: formValues.oneWayEnd.description,
      isAgency: false,
    };
  }

  return disponibilitiesQueryParams;
};
